import { authenticatedFetch } from "./apiClient";
import {
  ApiRequestError,
} from "../utils/apiRequestError";
import { parseApiErrorBody } from "../utils/parseApiError";
import type { ParsedApiError } from "../utils/parseApiError";
import {
  CreateReportBody,
  CreateReportStrategyBody,
  CreateReportStrategyPolicyVersionBody,
  GenerateReportBody,
  GenerateReportResult,
  PublishLatestRunPolicyVersionBody,
  Report,
  ReportListQuery,
  ReportListResponse,
  ReportStrategyDetailResponse,
  ReportStrategiesListQuery,
  ReportStrategiesListResponse,
  ReportStrategyResponse,
  ReportStrategyPolicyVersion,
  ReportStrategyPolicyVersionsListResponse,
  ReportTypesResponse,
  SetCurrentPolicyVersionBody,
  UpdateReportStrategyBody,
  UpdateReportStrategyResult,
  ReportStrategyPolicyVersionsListQuery,
  SetReportStrategyPolicyAccountsBody,
} from "../types/reports";
import type {
  CombinePoliciesBody,
  CombinePoliciesPreviewBody,
  CombinePoliciesPreviewResponse,
  CombinePoliciesResponse,
  ComposedPineDownloadErrorCode,
  ComposeReportStrategyPolicyBody,
  ComposeReportStrategyPolicyResponse,
  ReportStrategyPineParseResult,
  ReportStrategyPineRequirementsResponse,
  ReportStrategyPineScriptDownloadResponse,
} from "../types/reportStrategyPine";
import type {
  FederatedWorkspaceDetail,
  StrategyWorkspacesResponse,
} from "../types/workspace";
import {
  normalizeFederatedWorkspaceResponse,
  normalizeStrategyWorkspacesResponse,
} from "./workspacesApi";
import { parseReportApiDetail, parseReportApiEnvelope } from "../utils/reportsApiErrors";
import {
  MarketDataUnresolvedError,
  parseMarketDataUnresolvedDetail,
} from "../utils/marketDataResolution";
import {
  MarketDataUnavailableError,
  throwIfMarketDataUnavailable,
} from "../utils/marketDataUnavailable";
import { normalizeReportStrategyDetail } from "../utils/reportStrategyNormalize";
import type {
  AvailableInstrumentsQuery,
  AvailableInstrumentsResponse,
  CandleTimeframePreviewQuery,
  CandleTimeframePreviewResponse,
  ManagementCandidatePreviewRequest,
  ManagementCandidatePreviewResponse,
  ManagementSegmentCatalogResponse,
} from "../types/tradeManagementReport";

export class ReportsApiError extends ApiRequestError {
  /** Legacy alias for envelope slug (e.g. `report_inflight`, `symbol_required`). */
  get errorCode(): string {
    return this.slug;
  }

  constructor(message: string, status: number, parsed: ParsedApiError) {
    super(message, status, parsed);
    this.name = "ReportsApiError";
  }
}

export class ReportStrategyComposedPineError extends Error {
  status: number;
  code: ComposedPineDownloadErrorCode;
  composeInvalidatedReason?: string;

  constructor(
    message: string,
    status: number,
    code: ComposedPineDownloadErrorCode,
    composeInvalidatedReason?: string
  ) {
    super(message);
    this.name = "ReportStrategyComposedPineError";
    this.status = status;
    this.code = code;
    this.composeInvalidatedReason = composeInvalidatedReason;
  }
}

export class ReportStrategyPineCompileError extends Error {
  unsupported_paths: string[];
  unsupported_ops: string[];

  constructor(message: string, unsupported_paths?: unknown, unsupported_ops?: unknown) {
    super(message);
    this.name = "ReportStrategyPineCompileError";
    this.unsupported_paths = Array.isArray(unsupported_paths) ? (unsupported_paths.filter((x) => typeof x === "string") as string[]) : [];
    this.unsupported_ops = Array.isArray(unsupported_ops) ? (unsupported_ops.filter((x) => typeof x === "string") as string[]) : [];
  }
}

const buildQueryParams = (query?: ReportListQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (!query) return params;

  if (query.broker_account_code) params.append("broker_account_code", query.broker_account_code);
  if (query.start_date) params.append("start_date", query.start_date);
  if (query.end_date) params.append("end_date", query.end_date);
  if (query.timeframe) params.append("timeframe", query.timeframe);
  if (query.local_start_date) params.append("local_start_date", query.local_start_date);
  if (query.local_end_date) params.append("local_end_date", query.local_end_date);
  if (query.timezone) params.append("timezone", query.timezone);
  if (query.symbol) params.append("symbol", query.symbol);
  if (query.report_type) params.append("report_type", query.report_type);
  if (query.status) params.append("status", query.status);
  if (query.strategy_id) params.append("strategy_id", query.strategy_id);
  if (typeof query.limit === "number") params.append("limit", String(query.limit));
  if (typeof query.offset === "number") params.append("offset", String(query.offset));

  return params;
};

async function parseOrThrow(response: Response, fallbackMessage: string): Promise<any> {
  if (response.ok) return await response.json();

  if (response.status === 401) {
    throw new ReportsApiError("Session expired. Please log in again.", 401, {
      slug: "auth.token_expired",
      message: "Session expired. Please log in again.",
    });
  }

  const error = await response.json().catch(() => ({}));
  if (response.status === 422) {
    throwIfMarketDataUnavailable(response, error);
    const unresolved = parseMarketDataUnresolvedDetail(error);
    if (unresolved) {
      throw new MarketDataUnresolvedError(unresolved, 422);
    }
  }

  const parsed = parseApiErrorBody(
    error && typeof error === "object" ? (error as Record<string, unknown>) : {},
    fallbackMessage
  );

  let message = parsed.message?.trim() || fallbackMessage;
  if (response.status === 400 && !parsed.message?.trim()) {
    message = "Invalid request (e.g. mixed local calendar dates with ISO bounds).";
  }
  if (response.status === 501 && !parsed.message?.trim()) {
    message = "Report generation (TES) is not configured on the server.";
  }
  if (response.status === 503 && !parsed.message?.trim()) {
    message = "Report service is temporarily unavailable. Please try again.";
  }

  throw new ReportsApiError(message, response.status, { ...parsed, message });
}

export async function createReport(body: CreateReportBody): Promise<Report> {
  const response = await authenticatedFetch("/reports", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return await parseOrThrow(response, `Failed to create report: ${response.status}`);
}

export async function listReports(query?: ReportListQuery): Promise<ReportListResponse> {
  const params = buildQueryParams(query);
  const queryString = params.toString();
  const endpoint = queryString ? `/reports?${queryString}` : "/reports";

  const response = await authenticatedFetch(endpoint);
  return await parseOrThrow(response, `Failed to fetch reports: ${response.status}`);
}

export async function getReport(reportId: string): Promise<Report> {
  const response = await authenticatedFetch(`/reports/${encodeURIComponent(reportId)}`);
  return await parseOrThrow(response, `Failed to fetch report: ${response.status}`);
}

export async function updateReport(reportId: string, patch: Partial<CreateReportBody>): Promise<Report> {
  const response = await authenticatedFetch(`/reports/${encodeURIComponent(reportId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return await parseOrThrow(response, `Failed to update report: ${response.status}`);
}

export async function deleteReport(reportId: string): Promise<void> {
  const response = await authenticatedFetch(`/reports/${encodeURIComponent(reportId)}`, { method: "DELETE" });
  if (response.status === 204 || response.ok) return;
  await parseOrThrow(response, `Failed to delete report: ${response.status}`);
}

export async function generateReport(body: GenerateReportBody): Promise<GenerateReportResult> {
  const response = await authenticatedFetch("/reports/generate", {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (response.status === 202) {
    const report = (await response.json()) as Report;
    return { report, accepted: true };
  }

  const report = (await parseOrThrow(response, `Failed to generate report: ${response.status}`)) as Report;
  return { report, accepted: false };
}

function buildAvailableInstrumentsParams(query?: AvailableInstrumentsQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (!query) return params;
  if (query.broker_account_code) params.append("broker_account_code", query.broker_account_code);
  if (query.start_date) params.append("start_date", query.start_date);
  if (query.end_date) params.append("end_date", query.end_date);
  if (query.timeframe) params.append("timeframe", query.timeframe);
  if (query.local_start_date) params.append("local_start_date", query.local_start_date);
  if (query.local_end_date) params.append("local_end_date", query.local_end_date);
  if (query.timezone) params.append("timezone", query.timezone);
  if (query.trade_side_filter) params.append("trade_side_filter", query.trade_side_filter);
  if (query.report_type) params.append("report_type", query.report_type);
  return params;
}

export async function fetchAvailableInstruments(
  query?: AvailableInstrumentsQuery
): Promise<AvailableInstrumentsResponse> {
  const params = buildAvailableInstrumentsParams(query);
  const qs = params.toString();
  const endpoint = qs ? `/reports/available-instruments?${qs}` : "/reports/available-instruments";
  const response = await authenticatedFetch(endpoint);
  const data = await parseOrThrow(response, `Failed to fetch instruments: ${response.status}`);
  const instruments = Array.isArray(data?.instruments) ? data.instruments : Array.isArray(data) ? data : [];
  const selectionHint =
    typeof data?.selection_hint === "string"
      ? data.selection_hint
      : data?.selection_hint == null
        ? null
        : undefined;
  return {
    instruments,
    selection_hint: selectionHint,
    include_market_context_default: data?.include_market_context_default,
  };
}

function buildCandleTimeframePreviewParams(query: CandleTimeframePreviewQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.append("symbol", query.symbol);
  params.append("candle_timeframe", query.candle_timeframe);
  if (query.broker_account_code) params.append("broker_account_code", query.broker_account_code);
  if (query.start_date) params.append("start_date", query.start_date);
  if (query.end_date) params.append("end_date", query.end_date);
  if (query.timeframe) params.append("timeframe", query.timeframe);
  if (query.local_start_date) params.append("local_start_date", query.local_start_date);
  if (query.local_end_date) params.append("local_end_date", query.local_end_date);
  if (query.timezone) params.append("timezone", query.timezone);
  return params;
}

export async function fetchCandleTimeframePreview(
  query: CandleTimeframePreviewQuery
): Promise<CandleTimeframePreviewResponse> {
  const params = buildCandleTimeframePreviewParams(query);
  const response = await authenticatedFetch(
    `/reports/candle-timeframe-preview?${params.toString()}`
  );
  return (await parseOrThrow(
    response,
    `Failed to fetch candle timeframe preview: ${response.status}`
  )) as CandleTimeframePreviewResponse;
}

export async function fetchManagementSegmentCatalog(
  reportId: string
): Promise<ManagementSegmentCatalogResponse> {
  const response = await authenticatedFetch(
    `/reports/${encodeURIComponent(reportId)}/management-segment-catalog`
  );
  return await parseOrThrow(response, `Failed to fetch segment catalog: ${response.status}`);
}

export async function previewManagementCandidate(
  reportId: string,
  body: ManagementCandidatePreviewRequest
): Promise<ManagementCandidatePreviewResponse> {
  const response = await authenticatedFetch(
    `/reports/${encodeURIComponent(reportId)}/management-candidates/preview`,
    { method: "POST", body: JSON.stringify(body) }
  );
  return await parseOrThrow(response, `Failed to preview management candidate: ${response.status}`);
}

function buildManagementExplorerParams(query?: import("../types/managementExplorer").ManagementExplorerQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (!query) return params;
  if (typeof query.min_sample_size === "number") {
    params.append("min_sample_size", String(query.min_sample_size));
  }
  if (query.segment_kind) params.append("segment_kind", query.segment_kind);
  if (typeof query.losing_only === "boolean") {
    params.append("losing_only", String(query.losing_only));
  }
  const actionableExit =
    typeof query.actionable_exit_only === "boolean"
      ? query.actionable_exit_only
      : typeof query.actionable_only === "boolean"
        ? query.actionable_only
        : undefined;
  if (typeof actionableExit === "boolean") {
    params.append("actionable_exit_only", String(actionableExit));
  }
  if (query.sort) params.append("sort", query.sort);
  return params;
}

export async function fetchManagementExplorer(
  reportId: string,
  query?: import("../types/managementExplorer").ManagementExplorerQuery
): Promise<import("../types/managementExplorer").ManagementExplorerListResponse> {
  const params = buildManagementExplorerParams(query);
  const qs = params.toString();
  const endpoint = qs
    ? `/reports/${encodeURIComponent(reportId)}/management-explorer?${qs}`
    : `/reports/${encodeURIComponent(reportId)}/management-explorer`;
  const response = await authenticatedFetch(endpoint);
  const data = await parseOrThrow(response, `Failed to fetch management explorer: ${response.status}`);
  const slices = Array.isArray(data?.slices) ? data.slices : [];
  return {
    explorer: data?.explorer,
    slices,
    slice_count: typeof data?.slice_count === "number" ? data.slice_count : slices.length,
    schema_version: data?.schema_version,
  };
}

export async function fetchManagementExplorerSliceDetail(
  reportId: string,
  sliceId: string
): Promise<import("../types/managementExplorer").ManagementExplorerSliceDetailResponse> {
  const encodedId = encodeURIComponent(sliceId);
  const endpoint = `/reports/${encodeURIComponent(reportId)}/management-explorer/slices/${encodedId}`;
  const response = await authenticatedFetch(endpoint);
  return await parseOrThrow(response, `Failed to fetch slice detail: ${response.status}`);
}

export async function simulateExitPlacement(
  reportId: string,
  body: import("../types/managementExplorer").SimulateExitPlacementRequest
): Promise<{ status: number; body: unknown }> {
  const response = await authenticatedFetch(
    `/reports/${encodeURIComponent(reportId)}/simulate-exit-placement`,
    { method: "POST", body: JSON.stringify(body) }
  );
  const bodyJson = await parseOrThrow(response, `Failed to simulate exit placement: ${response.status}`);
  return { status: response.status, body: bodyJson };
}

export async function fetchExitPlacementComposePreview(
  reportId: string,
  body: import("../types/managementExplorer").ExitPlacementComposePreviewRequest
): Promise<import("../types/managementExplorer").ExitPlacementComposePreviewResponse> {
  const response = await authenticatedFetch(
    `/reports/${encodeURIComponent(reportId)}/exit-placement-compose-preview`,
    { method: "POST", body: JSON.stringify(body) }
  );
  return await parseOrThrow(response, `Failed to preview exit placement compose: ${response.status}`);
}

export async function composeExitPlacement(
  reportId: string,
  body: import("../types/managementExplorer").ExitPlacementComposeRequest
): Promise<import("../types/managementExplorer").ExitPlacementComposeResponse> {
  const response = await authenticatedFetch(
    `/reports/${encodeURIComponent(reportId)}/exit-placement-compose`,
    { method: "POST", body: JSON.stringify(body) }
  );
  return await parseOrThrow(response, `Failed to compose exit placement: ${response.status}`);
}

export async function getReportTypes(): Promise<ReportTypesResponse> {
  const response = await authenticatedFetch("/reports/types");
  return await parseOrThrow(response, `Failed to fetch report types: ${response.status}`);
}

const buildStrategiesQueryParams = (query?: ReportStrategiesListQuery): URLSearchParams => {
  const params = new URLSearchParams();
  if (!query) return params;

  if (typeof query.include_archived === "boolean") {
    params.append("include_archived", String(query.include_archived));
  }
  if (typeof query.limit === "number") params.append("limit", String(query.limit));
  if (typeof query.offset === "number") params.append("offset", String(query.offset));
  if (query.broker_account_code?.trim()) {
    params.append("broker_account_code", query.broker_account_code.trim());
  }
  if (query.broker_name?.trim()) {
    params.append("broker_name", query.broker_name.trim());
  }
  return params;
};

export async function listReportStrategies(
  query?: ReportStrategiesListQuery
): Promise<ReportStrategiesListResponse> {
  const params = buildStrategiesQueryParams(query);
  const queryString = params.toString();
  const endpoint = queryString ? `/report-strategies?${queryString}` : "/report-strategies";
  const httpResponse = await authenticatedFetch(endpoint);
  const raw = await parseOrThrow(
    httpResponse,
    `Failed to fetch report strategies: ${httpResponse.status}`
  );
  const strategies = Array.isArray((raw as ReportStrategiesListResponse)?.strategies)
    ? (raw as ReportStrategiesListResponse).strategies.map((row) => normalizeReportStrategyDetail(row))
    : [];
  return {
    ...(raw as ReportStrategiesListResponse),
    strategies,
  };
}

export async function createReportStrategy(
  body: CreateReportStrategyBody
): Promise<ReportStrategyResponse> {
  const response = await authenticatedFetch("/report-strategies", {
    method: "POST",
    body: JSON.stringify(body),
  });
  const raw = await parseOrThrow(response, `Failed to create strategy: ${response.status}`);
  return normalizeReportStrategyDetail(raw);
}

async function parseComposedPineDownloadOrThrow(
  response: Response,
  fallbackMessage: string
): Promise<ReportStrategyPineScriptDownloadResponse> {
  if (response.ok) {
    return (await response.json()) as ReportStrategyPineScriptDownloadResponse;
  }
  const error = await response.json().catch(() => ({}));
  const detail = (error as { detail?: unknown })?.detail;
  const detailObj =
    detail && typeof detail === "object" && !Array.isArray(detail)
      ? (detail as Record<string, unknown>)
      : null;
  const code =
    detailObj?.code === "no_composed_pine" || detailObj?.code === "compose_stale"
      ? detailObj.code
      : response.status === 404
        ? "no_composed_pine"
        : response.status === 409
          ? "compose_stale"
          : null;
  const message =
    (typeof detailObj?.message === "string" && detailObj.message) ||
    (typeof detail === "string" && detail) ||
    fallbackMessage;
  if (code) {
    throw new ReportStrategyComposedPineError(
      message,
      response.status,
      code,
      typeof detailObj?.compose_invalidated_reason === "string"
        ? detailObj.compose_invalidated_reason
        : undefined
    );
  }
  throw new ReportsApiError(message, response.status, {
    slug: "http_error",
    message,
  });
}

export async function updateReportStrategy(
  strategyId: string,
  patch: UpdateReportStrategyBody
): Promise<UpdateReportStrategyResult> {
  const response = await authenticatedFetch(`/report-strategies/${encodeURIComponent(strategyId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  const raw = await parseOrThrow(response, `Failed to update strategy: ${response.status}`);
  return normalizeReportStrategyDetail(raw) as UpdateReportStrategyResult;
}

/** Permanently delete a strategy and all policy versions (204). Reports keep strategy_id cleared. */
export async function deleteReportStrategy(strategyId: string): Promise<void> {
  const response = await authenticatedFetch(`/report-strategies/${encodeURIComponent(strategyId)}`, {
    method: "DELETE",
  });
  if (response.ok) return;
  await parseOrThrow(response, `Failed to delete strategy: ${response.status}`);
}

export async function getReportStrategyDetail(strategyId: string): Promise<ReportStrategyDetailResponse> {
  const response = await authenticatedFetch(`/report-strategies/${encodeURIComponent(strategyId)}`);
  const raw = await parseOrThrow(response, `Failed to fetch strategy: ${response.status}`);
  return normalizeReportStrategyDetail(raw);
}

export async function listReportStrategyPolicyVersions(
  strategyId: string,
  query?: ReportStrategyPolicyVersionsListQuery
): Promise<ReportStrategyPolicyVersionsListResponse> {
  const params = new URLSearchParams();
  if (query?.include_archived) params.set("include_archived", "true");
  if (query?.broker_name?.trim()) params.set("broker_name", query.broker_name.trim());
  if (query?.broker_account_code?.trim()) {
    params.set("broker_account_code", query.broker_account_code.trim());
  }
  const qs = params.toString();
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policy-versions${qs ? `?${qs}` : ""}`
  );
  const raw = await parseOrThrow(response, `Failed to fetch policy versions: ${response.status}`);
  // BE returns a raw array; normalize to `{ versions }` for all callers.
  if (Array.isArray(raw)) {
    return { versions: raw as ReportStrategyPolicyVersion[] };
  }
  const wrapped = raw as ReportStrategyPolicyVersionsListResponse | null;
  if (wrapped && Array.isArray(wrapped.versions)) {
    return { versions: wrapped.versions };
  }
  return { versions: [] };
}

/** Full-replace account tags on a policy / combined_policy version. */
export async function setReportStrategyPolicyAccounts(
  strategyId: string,
  policyVersionId: string,
  body: SetReportStrategyPolicyAccountsBody
): Promise<ReportStrategyPolicyVersion> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policies/${encodeURIComponent(policyVersionId)}/accounts`,
    {
      method: "PUT",
      body: JSON.stringify({
        accounts: body.accounts.map((account) => ({
          brokerName: account.brokerName.trim().toLowerCase(),
          brokerAccountCode: account.brokerAccountCode.trim(),
        })),
      }),
    }
  );
  const raw = await parseOrThrow(
    response,
    `Failed to update policy accounts: ${response.status}`
  );
  return raw as ReportStrategyPolicyVersion;
}

export async function updateReportStrategyPolicyVersion(
  strategyId: string,
  policyVersionId: string,
  body: { is_archived?: boolean; title?: string }
): Promise<ReportStrategyPolicyVersion> {
  if (body.is_archived === undefined && body.title === undefined) {
    throw new Error("Policy version update requires is_archived and/or title.");
  }
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policy-versions/${encodeURIComponent(policyVersionId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(body),
    }
  );
  const raw = await parseOrThrow(response, `Failed to update policy version: ${response.status}`);
  return raw as ReportStrategyPolicyVersion;
}

export async function createReportStrategyPolicyVersion(
  strategyId: string,
  body: CreateReportStrategyPolicyVersionBody
): Promise<any> {
  const response = await authenticatedFetch(`/report-strategies/${encodeURIComponent(strategyId)}/policy-versions`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return await parseOrThrow(response, `Failed to create policy version: ${response.status}`);
}

export async function publishLatestRunAsPolicyVersion(
  strategyId: string,
  body?: PublishLatestRunPolicyVersionBody
): Promise<any> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policy-versions/publish-latest-run`,
    {
      method: "POST",
      body: JSON.stringify(body ?? {}),
    }
  );
  return await parseOrThrow(response, `Failed to publish latest run: ${response.status}`);
}

export async function setCurrentReportStrategyPolicyVersion(
  strategyId: string,
  body: SetCurrentPolicyVersionBody
): Promise<any> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policy-versions/set-current`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
  return await parseOrThrow(response, `Failed to set current policy version: ${response.status}`);
}

export async function deleteReportStrategyPolicyVersion(
  strategyId: string,
  policyVersionId: string
): Promise<any> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policy-versions/${encodeURIComponent(policyVersionId)}`,
    { method: "DELETE" }
  );
  return await parseOrThrow(response, `Failed to delete policy version: ${response.status}`);
}

export type ReportStrategyPolicyVersionPineResponse = {
  pinescript: string;
  meta?: Record<string, unknown>;
  supported_paths?: string[];
  supported_ops?: string[];
};

export async function getReportStrategyPolicyVersionPineScript(
  strategyId: string,
  policyVersionId: string,
  opts?: { policy_row_version?: string; label_style?: "descriptive" | "minimal" }
): Promise<ReportStrategyPolicyVersionPineResponse> {
  const params = new URLSearchParams();
  if (opts?.policy_row_version) params.set("policy_row_version", opts.policy_row_version);
  if (opts?.label_style) params.set("label_style", opts.label_style);
  const qs = params.toString();
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policy-versions/${encodeURIComponent(policyVersionId)}/pinescript${
      qs ? `?${qs}` : ""
    }`
  );
  if (response.ok) return (await response.json()) as ReportStrategyPolicyVersionPineResponse;

  if (response.status === 422) {
    const payload = await response.json().catch(() => ({}));
    const detail = (payload as any)?.detail;
    const msg =
      typeof detail?.message === "string"
        ? detail.message
        : "Cannot compile policy to Pinescript: unsupported paths/ops.";
    throw new ReportStrategyPineCompileError(msg, detail?.unsupported_paths, detail?.unsupported_ops);
  }

  return await parseOrThrow(response, `Failed to fetch Pinescript: ${response.status}`);
}

export async function parseReportStrategyPinePreview(
  pinescript: string
): Promise<ReportStrategyPineParseResult> {
  const response = await authenticatedFetch("/report-strategies/parse-preview", {
    method: "POST",
    body: JSON.stringify({ pinescript }),
  });
  return await parseOrThrow(response, `Failed to validate Pinescript: ${response.status}`);
}

export async function getReportStrategyPineRequirements(): Promise<ReportStrategyPineRequirementsResponse> {
  const response = await authenticatedFetch("/report-strategies/pine-requirements");
  return await parseOrThrow(response, `Failed to load Pinescript requirements: ${response.status}`);
}

export async function previewCombineReportStrategyPolicies(
  strategyId: string,
  body: CombinePoliciesPreviewBody
): Promise<CombinePoliciesPreviewResponse> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policies/combine-preview`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
  return await parseOrThrow(response, `Failed to preview policy combine: ${response.status}`);
}

export async function combineReportStrategyPolicies(
  strategyId: string,
  body: CombinePoliciesBody
): Promise<CombinePoliciesResponse> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/policies/combine`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
  return await parseOrThrow(response, `Failed to combine policies: ${response.status}`);
}

/** Account canvases that contain a node for this strategy. */
export async function listStrategyWorkspaces(
  strategyId: string
): Promise<StrategyWorkspacesResponse> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/workspaces`
  );
  const data = await parseOrThrow(
    response,
    `Failed to list strategy workspaces: ${response.status}`
  );
  return normalizeStrategyWorkspacesResponse(data);
}

/** Synthetic Strategy canvas merging all contributing account workspaces. */
export async function getFederatedWorkspace(
  strategyId: string
): Promise<FederatedWorkspaceDetail> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/federated-workspace`
  );
  const data = await parseOrThrow(
    response,
    `Failed to load strategy workspace: ${response.status}`
  );
  return normalizeFederatedWorkspaceResponse(data);
}

export async function composeReportStrategyPolicy(
  strategyId: string,
  body: ComposeReportStrategyPolicyBody
): Promise<ComposeReportStrategyPolicyResponse> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/compose-policy`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );
  return await parseOrThrow(response, `Failed to compose policy into Pinescript: ${response.status}`);
}

export async function getReportStrategyComposedPinescript(
  strategyId: string
): Promise<ReportStrategyPineScriptDownloadResponse> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/composed-pinescript`
  );
  return await parseComposedPineDownloadOrThrow(
    response,
    `Failed to load composed Pinescript: ${response.status}`
  );
}

/** Remove persisted composed / indicator Pinescript (generated TradingView export). */
export async function deleteReportStrategyComposedPinescript(
  strategyId: string
): Promise<UpdateReportStrategyResult> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/composed-pinescript`,
    { method: "DELETE" }
  );
  if (response.status === 204) {
    return getReportStrategyDetail(strategyId);
  }
  const fallbackMessage =
    response.status === 405
      ? "Remove generated Pinescript is not available on this API yet — restart the backend with the latest code."
      : `Failed to remove composed Pinescript: ${response.status}`;
  const raw = await parseOrThrow(response, fallbackMessage);
  return normalizeReportStrategyDetail(raw) as UpdateReportStrategyResult;
}

export async function getReportStrategyIndicatorPinescript(
  strategyId: string
): Promise<ReportStrategyPineScriptDownloadResponse> {
  const response = await authenticatedFetch(
    `/report-strategies/${encodeURIComponent(strategyId)}/indicator-pinescript`
  );
  return await parseComposedPineDownloadOrThrow(
    response,
    `Failed to load indicator Pinescript: ${response.status}`
  );
}

export type ReportSymbolsResponse = {
  report_id: string;
  symbols: string[];
};

export async function getReportSymbols(reportId: string): Promise<ReportSymbolsResponse> {
  const response = await authenticatedFetch(`/reports/${encodeURIComponent(reportId)}/symbols`);
  return await parseOrThrow(response, `Failed to fetch report symbols: ${response.status}`);
}

export type {
  CohortTradesResolveRequestBody,
  CohortTradesResolveResponse,
} from "../utils/cohortTradesResolve";

export async function resolveCohortTrades(
  reportId: string,
  body: import("../utils/cohortTradesResolve").CohortTradesResolveRequestBody,
  query?: { offset?: number; limit?: number }
): Promise<import("../utils/cohortTradesResolve").CohortTradesResolveResponse> {
  const params = new URLSearchParams();
  if (typeof query?.offset === "number") params.append("offset", String(query.offset));
  if (typeof query?.limit === "number") params.append("limit", String(query.limit));
  const qs = params.toString();
  const endpoint = qs
    ? `/reports/${encodeURIComponent(reportId)}/cohort-trades/resolve?${qs}`
    : `/reports/${encodeURIComponent(reportId)}/cohort-trades/resolve`;
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return await parseOrThrow(response, `Failed to resolve cohort trades: ${response.status}`);
}

export type {
  PolicyPickOverlapRequest,
  PolicyPickOverlapResponse,
  PolicyPickOverlapQuery,
} from "../types/policyPickOverlap";

export async function checkPolicyPickOverlap(
  reportId: string,
  body: import("../types/policyPickOverlap").PolicyPickOverlapRequest,
  query?: import("../types/policyPickOverlap").PolicyPickOverlapQuery
): Promise<import("../types/policyPickOverlap").PolicyPickOverlapResponse> {
  const params = new URLSearchParams();
  if (typeof query?.max_subset_size === "number") {
    params.append("max_subset_size", String(query.max_subset_size));
  }
  if (query?.include_trade_ids) params.append("include_trade_ids", "true");
  if (typeof query?.offset === "number") params.append("offset", String(query.offset));
  if (typeof query?.limit === "number") params.append("limit", String(query.limit));
  const qs = params.toString();
  const endpoint = qs
    ? `/reports/${encodeURIComponent(reportId)}/policy-picks/overlap?${qs}`
    : `/reports/${encodeURIComponent(reportId)}/policy-picks/overlap`;
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return await parseOrThrow(response, `Failed to check policy pick overlap: ${response.status}`);
}
